import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GUIDES } from "../data/guidesData";

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  serif: "'Instrument Serif', Georgia, serif",
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const CATEGORIES = ["All", ...Array.from(new Set(GUIDES.map(g => g.category)))];

export default function GuidesIndexPage() {
  useFonts();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("All");

  const filtered = activeCategory === "All"
    ? GUIDES
    : GUIDES.filter(g => g.category === activeCategory);

  const featured = GUIDES[0];
  const rest = filtered.filter(g => g.id !== featured.id);
  const showFeatured = activeCategory === "All";

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900 }}>
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { display: none; }
        .guide-card-hover { transition: border-color .18s, box-shadow .18s; }
        .guide-card-hover:hover { border-color: ${T.g200} !important; box-shadow: 0 4px 24px rgba(30,146,84,.08); }
        .guide-img { transition: transform .4s cubic-bezier(.25,.46,.45,.94); }
        .guide-card-hover:hover .guide-img { transform: scale(1.04); }
        .read-link { transition: color .15s; }
        .read-link:hover { color: ${T.g700} !important; }
      `}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = T.n800}
          onMouseLeave={e => e.currentTarget.style.color = T.n400}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
          Back
        </button>
        <button onClick={() => navigate("/pro-onboarding")} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: T.g500, border: "none", cursor: "pointer", color: T.white, fontSize: 13, fontFamily: T.sans, padding: "8px 16px", borderRadius: 8, fontWeight: 600 }}
          onMouseEnter={e => e.currentTarget.style.background = T.g600}
          onMouseLeave={e => e.currentTarget.style.background = T.g500}>
          Become a JiraniPro
        </button>
      </div>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(52px,7vw,88px) 20px clamp(40px,5vw,64px)", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 20 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.g500} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
            JiraniPro Guides
          </div>
          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(38px,7vw,62px)", fontWeight: 400, margin: "0 0 16px", lineHeight: 1.07, color: T.n900, letterSpacing: "-0.03em", maxWidth: 560 }}>
            Everything you need to<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>thrive on the platform</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.75, margin: 0, maxWidth: 480 }}>
            Practical guides written for JiraniPros, covering everything from landing your first jobs to building a full-time professional income.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 760, margin: "0 auto", padding: "0 20px clamp(80px,10vw,120px)" }}>

        {/* ── Category filter ── */}
        <div style={{ paddingTop: "clamp(32px,5vw,48px)", marginBottom: "clamp(36px,5vw,52px)", display: "flex", gap: 8, flexWrap: "wrap" }}>
          {CATEGORIES.map(cat => {
            const active = activeCategory === cat;
            return (
              <button key={cat} onClick={() => setActiveCategory(cat)} style={{
                padding: "8px 16px", borderRadius: 99, fontSize: 13, fontWeight: active ? 600 : 400,
                border: `1.5px solid ${active ? T.g500 : T.n150}`,
                background: active ? T.g500 : T.white,
                color: active ? T.white : T.n600,
                cursor: "pointer", fontFamily: T.sans, transition: "all .15s",
              }}>
                {cat}
              </button>
            );
          })}
        </div>

        {/* ── Featured guide (first guide, shown only on All) ── */}
        {showFeatured && (
          <div className="guide-card-hover" onClick={() => navigate(`/guides/${featured.id}`)}
            style={{ marginBottom: "clamp(40px,6vw,56px)", borderRadius: 20, border: `1.5px solid ${T.n150}`, overflow: "hidden", cursor: "pointer" }}>
            <div style={{ height: "clamp(200px,35vw,320px)", background: featured.color, overflow: "hidden" }}>
              <img src={featured.image} alt={featured.title} className="guide-img"
                onError={e => { e.target.style.display = "none"; }}
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
            <div style={{ padding: "clamp(24px,4vw,36px)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: T.g600, background: T.g50, border: `1px solid ${T.g100}`, borderRadius: 99, padding: "3px 10px", letterSpacing: "0.05em", textTransform: "uppercase" }}>{featured.category}</span>
                <span style={{ fontSize: 12, color: T.n400 }}>{featured.readTime}</span>
              </div>
              <h2 style={{ fontFamily: T.serif, fontSize: "clamp(22px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "0 0 12px", letterSpacing: "-0.025em", lineHeight: 1.15 }}>{featured.title}</h2>
              <p style={{ fontSize: 15, color: T.n600, lineHeight: 1.75, margin: "0 0 20px", maxWidth: 520 }}>{featured.description}</p>
              <span className="read-link" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, color: T.g500, background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: T.sans }}>
                Read guide
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.g500} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </span>
            </div>
          </div>
        )}

        {/* ── Guide grid ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 20 }}>
          {(showFeatured ? rest : filtered).map(guide => (
            <div key={guide.id} className="guide-card-hover"
              onClick={() => navigate(`/guides/${guide.id}`)}
              style={{ borderRadius: 16, border: `1.5px solid ${T.n150}`, overflow: "hidden", cursor: "pointer", background: T.white, display: "flex", flexDirection: "column" }}>
              <div style={{ height: 180, background: guide.color, overflow: "hidden", flexShrink: 0 }}>
                <img src={guide.image} alt={guide.title} className="guide-img"
                  onError={e => { e.target.style.display = "none"; }}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </div>
              <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: T.g600, background: T.g50, border: `1px solid ${T.g100}`, borderRadius: 99, padding: "2px 9px", letterSpacing: "0.05em", textTransform: "uppercase" }}>{guide.category}</span>
                  <span style={{ fontSize: 11, color: T.n400 }}>{guide.readTime}</span>
                </div>
                <h3 style={{ fontFamily: T.serif, fontSize: "clamp(16px,2.5vw,19px)", fontWeight: 400, color: T.n900, margin: 0, letterSpacing: "-0.02em", lineHeight: 1.25 }}>{guide.title}</h3>
                <p style={{ fontSize: 13, color: T.n600, lineHeight: 1.7, margin: 0, flex: 1 }}>{guide.description}</p>
                <span className="read-link" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 600, color: T.g500, marginTop: 4 }}>
                  Read guide
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.g500} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* ── Footer CTA ── */}
        <div style={{ marginTop: "clamp(56px,8vw,80px)", padding: "clamp(32px,5vw,48px)", borderRadius: 20, border: `1px solid ${T.n150}`, textAlign: "center", background: T.n50 }}>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,34px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Ready to put this into <em style={{ fontStyle: "italic", color: T.g600 }}>practice?</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 28px", lineHeight: 1.75, maxWidth: 380, marginLeft: "auto", marginRight: "auto" }}>
            Create your JiraniPro profile and start applying what you have learned from day one.
          </p>
          <button onClick={() => navigate("/pro-onboarding")} style={{
            padding: "13px 28px", background: T.g500, color: T.white,
            border: "none", borderRadius: 11, fontSize: 14, fontWeight: 600,
            fontFamily: T.sans, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8,
          }}
            onMouseEnter={e => e.currentTarget.style.background = T.g600}
            onMouseLeave={e => e.currentTarget.style.background = T.g500}>
            Become a JiraniPro
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.white} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          </button>
        </div>

      </div>
    </div>
  );
}